import { getDefaultConfig } from '.';
import { MVTLayer } from '@deck.gl/geo-layers';
import type { Color } from '@deck.gl/core';
import type { LayerConfig } from './types';
import { DEFAULT_TREE_COVER_MIN_ZOOM, LayerType } from './types';
import { handleTreeSpecies } from '@/views/map/inspect/utils';
import type { Species } from '@/features/fieldSlice';
import type { Dispatch, SetStateAction } from 'react';
import type { HoverInfo } from '@/views/MapView';
import type { Feature } from 'geojson';

const getSpecieColor = (
  d: Feature,
  type: string,
  updatedSpecies: Species[]
): Color => {
  const STROKE_ALPHA = 255;
  const OTHER_STROKE_ALPHA = type === 'line' ? 0 : 80;
  const EVERGREEN_STROKE_COLOR: Color = [6, 223, 27, OTHER_STROKE_ALPHA];
  const DECIDUOUS_STROKE_COLOR: Color = [250, 65, 253, OTHER_STROKE_ALPHA];
  // TODO: So for species id 1 and 2 we totally ignore the colors defined in updatedSpecies?
  const speciesId = d.properties?.species_id;
  if (speciesId === 1) {
    return DECIDUOUS_STROKE_COLOR;
  }
  if (speciesId === 2) {
    return EVERGREEN_STROKE_COLOR;
  }
  if (speciesId > 2) {
    const getSpecie = updatedSpecies.filter(
      (specie) => specie.id === speciesId
    );
    if (getSpecie[0].color) {
      const { r, g, b, a } = getSpecie[0].color;
      return [r, g, b, type === 'line' ? STROKE_ALPHA : a];
    }
  }
  return [0, 0, 0, STROKE_ALPHA];
};

export const getSpeciesLayer = (
  config: LayerConfig,
  updatedSpecies: Species[],
  setHoverInfo: Dispatch<SetStateAction<HoverInfo>>,
  isInspectMode: boolean,
  user: string,
  minZoom = DEFAULT_TREE_COVER_MIN_ZOOM,
  maxZoom = 18
) => {
  const id = LayerType[LayerType.tree_species];
  return new MVTLayer({
    ...getDefaultConfig(id, user),
    id,
    visible: isInspectMode || config.visible,
    opacity: isInspectMode && !config.visible ? 0 : 1,
    pickable: true,
    minZoom,
    maxZoom,
    stroked: true,
    binary: false,
    refinementStrategy: 'no-overlap',
    getLineWidth: 10,
    onHover: (i) => handleTreeSpecies(i, setHoverInfo),
    getFillColor: (d) => getSpecieColor(d, 'fill', updatedSpecies),
    getLineColor: (d) => getSpecieColor(d, 'line', updatedSpecies),
    lineWidthScale: 15,
    lineJointRounded: true,
    lineWidthMaxPixels: 1,
  });
};
